import type { Team } from '../../schemas/common';
import type { GameEvent } from '../../schemas/event';
import { COV, type GameDepth, type GameMode } from '../../schemas/game';
import { RULE_PRESETS, type Rules } from '../../schemas/rules';
import type { GameState } from './replay';

/**
 * Small pure helpers around the fold that the pad, the board, the summary and the server all use.
 * Owner: games.
 */

/** The effective event Undo would void: the newest one, except an `end` (finished games never reopen). */
export function undoTarget(state: Pick<GameState, 'effective'>): GameEvent | null {
  const last = state.effective[state.effective.length - 1];
  return last && last.type !== 'end' ? last : null;
}

/** Score difference (A minus B) after each scoring event, for the worm chart. Starts at 0. */
export function scoreWorm(state: Pick<GameState, 'log'>): { diff: number; scoreA: number; scoreB: number }[] {
  const out = [{ diff: 0, scoreA: 0, scoreB: 0 }];
  for (const l of state.log) out.push({ diff: l.scoreA - l.scoreB, scoreA: l.scoreA, scoreB: l.scoreB });
  return out;
}

/** The newest scoring entry, for the board's "Nassos +2" flash. */
export function lastScore(state: Pick<GameState, 'log'>) {
  return state.log.length ? (state.log[state.log.length - 1] ?? null) : null;
}

/**
 * Field goals made in a row by one player with nobody else scoring in between for that team
 * (the "on fire" flame after 3). A miss by the player ends the run. Needs misses tracked to see
 * misses, otherwise it counts the team's unbroken run of that player's buckets.
 */
export function hotStreak(state: Pick<GameState, 'effective' | 'players'>, playerId: string): number {
  const team = state.players[playerId]?.team;
  if (!team) return 0;
  let run = 0;
  for (let i = state.effective.length - 1; i >= 0; i--) {
    const e = state.effective[i] as GameEvent;
    if (e.type !== 'shot') continue;
    const shooter = state.players[e.playerId];
    if (!shooter || shooter.team !== team) continue;
    if (e.playerId === playerId && e.data.made) run += 1;
    else break;
  }
  return run;
}

/** Consecutive unanswered points by one team at the end of the log (a run). */
export function currentRun(state: Pick<GameState, 'log'>): { team: Team; points: number } | null {
  const last = state.log[state.log.length - 1];
  if (!last) return null;
  let points = 0;
  for (let i = state.log.length - 1; i >= 0; i--) {
    const l = state.log[i];
    if (!l || l.team !== last.team) break;
    points += l.points;
  }
  return points > 0 ? { team: last.team, points } : null;
}

/** Coverage bits to a depth label. 0 is `final`, makes and free throws only is `quick`, the rest `full`. */
export function depthOf(coverage: number): GameDepth {
  if (coverage === 0) return 'final';
  return (coverage & ~(COV.MAKES | COV.FT)) === 0 ? 'quick' : 'full';
}

/** Bits a 1v1 pad has no way to write: assists need a teammate, and the side buttons carry no stat chips. */
const SOLO_BLIND = COV.AST | COV.REB | COV.STL | COV.BLK | COV.TOV | COV.PF;

/**
 * Drop the coverage bits a format cannot record. A 1v1 (and honor mode, which is a 1v1) has no assist
 * teammates and no stat chips, so claiming them would make the box score show honest-looking zeros for
 * things nobody could count (stats.md: nobody is credited with 0 for something nobody counted).
 */
export function maskCoverage(mode: GameMode, coverage: number): number {
  return mode === '1v1' ? coverage & ~SOLO_BLIND : coverage;
}

/** Build the coverage mask from the setup toggles. `misses` also covers free throw misses. */
export function coverageFrom(o: {
  misses?: boolean;
  assists?: boolean;
  stats?: boolean;
  lineup?: boolean;
  freeThrows?: boolean;
  /** When given, bits this format cannot record are dropped (see `maskCoverage`). */
  mode?: GameMode;
}): number {
  let c: number = COV.MAKES;
  if (o.freeThrows !== false) c |= COV.FT;
  if (o.misses) c |= COV.MISSES;
  if (o.assists) c |= COV.AST;
  if (o.stats) c |= COV.REB | COV.STL | COV.BLK | COV.TOV | COV.PF;
  if (o.lineup) c |= COV.LINEUP;
  return o.mode ? maskCoverage(o.mode, c) : c;
}

/** The rules a new game of this size starts with: 1v1 and 2v2 to 11 in ones and twos, bigger to 21 in twos and threes. */
export function defaultRulesFor(mode: GameMode): Rules {
  const preset = mode === '1v1' || mode === '2v2' ? RULE_PRESETS.quick11 : RULE_PRESETS.to21;
  return { ...preset };
}

/** "2v2 to 21", or "2v2 timed" for a clock game. Plain data for lists and sessions' attached items. */
export function gameTitle(mode: string, rules: Pick<Rules, 'target' | 'clock'>): string {
  return rules.clock ? `${mode} timed` : `${mode} to ${rules.target}`;
}
